import { aliasTo, asClass, asValue, Constructor, createContainer } from 'awilix';

import { CoreModule } from './CoreModule';
import {
  BaseModule,
  Disposable,
  InjectableConstructor,
  INJECTION_TOKEN,
  ModuleApi,
  ModuleConstructor,
  MODULES_TOKEN,
  SystemConstructor,
  SYSTEMS_TOKEN,
} from './injectable';
import { BaseEngineOptions, ENGINE_OPTIONS_TOKEN, ENGINE_TOKEN, EngineConstructor } from './injectable/Engine';
import { Zed } from './Zed';

/**
 * A builder class for creating instances of modules with their APIs.
 *
 * @example
 * ```ts
 * import { ZedBuilder } from 'core';
 * import { BuildingModule } from 'building';
 * import { InteriorModule } from 'interior';
 *
 * const zed = new ZedBuilder()
 *  .addModule(BuildingModule)
 *  .addModule(InteriorModule);
 *  .build();
 *
 * console.log(zed.api.building);
 * ```
 */
export class ZedBuilder<ModuleApiMap extends Record<string, any> = {}> {
  private container = createContainer();
  private modules = new Set<ModuleConstructor>();
  private injections = new Set<InjectableConstructor>();
  private systems = new Set<SystemConstructor>();

  constructor(engine: EngineConstructor, engineOptions: BaseEngineOptions) {
    this.container.register(ENGINE_TOKEN, aliasTo(engine.INJECTABLE_TOKEN));

    this.addValue(ENGINE_OPTIONS_TOKEN, engineOptions);
    this.addModule(engine);
    this.addModule(CoreModule);
  }

  addValue<T>(name: string | symbol, value: T) {
    this.container.register(name, asValue(value));

    return this;
  }

  /**
   * Adds a module and its services to the ZedBuilder instance.
   * @param moduleConstructor The constructor of the module to add.
   * @returns The updated ZedBuilder instance.
   */
  addModule<Module extends BaseModule>(moduleConstructor: ModuleConstructor<Module>) {
    this.modules.add(moduleConstructor);

    moduleConstructor[INJECTION_TOKEN]?.forEach((injectableConstructor) => {
      this.injections.add(injectableConstructor);
    });

    moduleConstructor[SYSTEMS_TOKEN]?.forEach((systemConstructor) => {
      this.systems.add(systemConstructor);
    });

    return this as ZedBuilder as ZedBuilder<ModuleApiMap & ModuleApi<Module>>;
  }

  /**
   * Builds a new instance of Zed with the registered modules and systems.
   * @returns A new instance of Zed.
   */
  build(): Zed<ModuleApiMap> {
    [...this.modules, ...this.injections, ...this.systems].forEach(
      <T, C extends InjectableConstructor<T>>(constructor: C) => {
        this.container.register(
          constructor.INJECTABLE_TOKEN,
          asClass(constructor, {
            dispose: constructor.prototype.dispose && ((injectable: Disposable) => injectable.dispose()),
            lifetime: 'SINGLETON',
          })
        );
      }
    );

    this.container.register(MODULES_TOKEN, {
      resolve: (container) => [...this.modules].map((m) => container.resolve(m.INJECTABLE_TOKEN)),
    });

    this.container.register(SYSTEMS_TOKEN, {
      resolve: (container) => [...this.systems].map((m) => container.resolve(m.INJECTABLE_TOKEN)),
    });

    return new Zed(this.container);
  }
}
